//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

// #region Imports
import { EthAddr, TxHash } from '@shared/SchemaTypes'
import { Contract, Signer } from 'ethers'

import { Dayjs } from 'dayjs'

import { ThunkAction, UnknownAction } from '@reduxjs/toolkit'
import { PoolFlexAbi } from '@shared/Abis'
import { ActivityEnum, RoleEnum } from '@shared/Enums'

import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'
import { poolActions } from '@react/redux/poolSlice'
import { OtStoreState, assertRole, dispatchError } from '@react/redux/store'
import { logOT } from '@shared/loggingShared'
import { otAssert } from '@shared/conversions'

export const updateFlexPoolNonBusinessDaysTx =
  (props: { poolAddr: EthAddr; nonBusinessDays: Dayjs[] }): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const activity = ActivityEnum.WITHDRAW_REQUEST

    try {
      const { webSocket, userConnection } = getState().chainState
      const { chainConfigurationName } = getState().chainState.chainConfiguration
      otAssert(userConnection, 'userConnection is not defined')
      const { roleEnum, signer } = userConnection
      assertRole(roleEnum, RoleEnum.POOL_ADMIN)
      const { poolAddr, nonBusinessDays } = props

      logOT.debug('updateFlexPoolNonBusinessDaysTx', poolAddr)

      await updateFlexPoolNonBusinessDays(
        webSocket,
        signer as Signer,
        poolAddr,
        nonBusinessDays,
        chainConfigurationName
      )
      dispatch(poolActions.updateNonBusinessDaysSuccess(props))
    } catch (e: any) {
      dispatch(poolActions.updateNonBusinessDaysFail(props))
      await dispatchError(dispatch, e, activity, 1016)
    }
  }

export const updateFlexPoolNonBusinessDays = async (
  webSocket: WebSocket | undefined,
  accountSigner: Signer,
  poolAddr: EthAddr,
  nonBusinessDays: Dayjs[],
  chainConfigurationName: string
) => {
  const uuid = txUUID()
  webSocket?.send(wsServerActionPoolTXRequest(ActivityEnum.WITHDRAW_REQUEST, poolAddr, uuid, chainConfigurationName))
  const data = nonBusinessDays.map((d: Dayjs) => Date.UTC(d.year(), d.month(), d.date()) / 1000)
  const poolFlexContract = new Contract(poolAddr, PoolFlexAbi, accountSigner)

  const tx = await poolFlexContract.updateNonBusinessDays(data)

  webSocket?.send(
    wsServerActionPoolTX(ActivityEnum.WITHDRAW_REQUEST, poolAddr, uuid, tx.hash as TxHash, chainConfigurationName)
  )

  const receipt = await tx.wait(1)
  return receipt
}
